import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  type AppPaletteMode,
  type ThemeVariant,
} from "@/constants/theme-variant";
import {
  DEFAULT_THEME_MODE,
  DEFAULT_THEME_STYLE,
  loadThemePreference,
  normalizeThemeMode,
  normalizeThemeStyle,
  sanitizeColorModeState,
} from "@/constants/theme-preference";

export interface ColorModeState {
  mode: AppPaletteMode;
  themeVariant: ThemeVariant;
}

const loaded =
  typeof window !== "undefined"
    ? loadThemePreference()
    : { themeStyle: DEFAULT_THEME_STYLE, themeMode: DEFAULT_THEME_MODE };

/**
 * colorMode is no longer redux-persisted; strip stale nested key from `persist:root`
 * so a future whitelist change cannot rehydrate corrupt theme values.
 */
function stripLegacyColorModeFromPersistRoot(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem("persist:root");
    if (!raw) return;
    const root = JSON.parse(raw) as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(root, "colorMode")) return;
    delete root.colorMode;
    localStorage.setItem("persist:root", JSON.stringify(root));
  } catch {
    // Malformed persist blob — leave for other migrations; theme keys are separate.
  }
}

if (typeof window !== "undefined") {
  stripLegacyColorModeFromPersistRoot();
}

const initialState: ColorModeState = {
  mode: loaded.themeMode,
  themeVariant: loaded.themeStyle,
};

export const colorModeSlice = createSlice({
  name: "colorMode",
  initialState,
  reducers: {
    updateColorMode: (state, action: PayloadAction<AppPaletteMode | string>) => {
      state.mode = normalizeThemeMode(action.payload);
    },
    updateThemeVariant: (state, action: PayloadAction<ThemeVariant | string>) => {
      state.themeVariant = normalizeThemeStyle(action.payload);
    },
    /**
     * Sanitize any legacy / corrupted colorMode payload (e.g. old persist:root shapes).
     * Maps bare `light`/`dark`/`amber`/`blue` into the safe preference shape.
     */
    hydrateColorMode: (state, action: PayloadAction<unknown>) => {
      const safe = sanitizeColorModeState(action.payload);
      state.mode = safe.themeMode;
      state.themeVariant = safe.themeStyle;
    },
  },
});

export const { updateColorMode, updateThemeVariant, hydrateColorMode } =
  colorModeSlice.actions;

export default colorModeSlice.reducer;
